/* eslint-disable global-require */
import React, { useEffect, useState } from "react";
import { Col, Form, message, Row } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import useStripeSubscription from "hooks/useStripeSubscription";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import moment from "moment";

// graphql
import STUDENT_REGISTRATION from "graphql/mutations/StudentRegistration";
import UPDATE_PROFILE from "graphql/mutations/UpdateProfile";
import CREATE_ONE_TIME_PAYMENT from "graphql/mutations/CreateOneTimePayment";
import SET_PM_AS_DEFAULT from "graphql/mutations/SetPmAsDefault";
import CREATE_TRIAL_PACKAGE from "graphql/mutations/CreateTrialPackage";
import GET_PROGRAM from "graphql/queries/GetProgram";
import STUDENT_REGISTRATION_WITH_OAUTH from "graphql/mutations/StudentRegistrationWithOauth";
import VNPAY_CREATE_PAYMENT from "graphql/mutations/VnpayCreatePayment";

import GET_LANGUAGES from "graphql/queries/GetLanguages";

import useErrors from "hooks/useErrors";
import useModal from "hooks/useModal";
import ErrorAlerting from "common/error_alerting";

// custom components
import { Options } from "scroll-into-view-if-needed";
import { authenticate, getUserData, UserData } from "utils/auth";
import { setLanguageDataVar } from "cache";
import { trackTapfiliateConversion } from "utils/tapfiliate";
import TermsConditionsModal from "common/modal/terms_conditions_modal/TermsConditionsModal";

// type
import { Program } from "types";
import {
  trackAddPaymentInfo,
  trackAddToCartEvent,
  trackNewUserRegistration,
  trackNewUserRegistrationWithCreditCard,
  trackPurchase,
} from "utils/gtm";

// styling
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import { CLASS_TYPES } from "utils/constants";
import { CustomStep, CustomSteps, PageContent } from "./SignUpStyle";
import SignUpForm from "./SignUpForm";
import PaymentForm from "./PaymentForm";
import AfterRegistration from "./after_sign_up/AfterRegistration";
import currencies from "./utils/currency";
import FreeTrialModal from "./FreeTrialModal";
import SocialLogin from "./SocialLogin";
import RegistrationSurvey from "./registration_survey";

// Define proper type for pending payment data
interface PendingPaymentData {
  paymentMethodId: string;
  promoCode: string;
  user: UserData | undefined;
}

type registrationFormValues = {
  locale?: string;
  givenName: string;
  familyName: string;
  email: string;
  password: string;
  confirmPassword: string;
  currentLevelId: number;
  currentStudyLanguage: string;
  preference?: string;
  agreedPrivacyPolicy?: boolean;
  sendTips?: boolean;
  spokenOnly?: boolean;
};

type parsedRegistrationFormValues = {
  locale?: string;
  givenName: string;
  familyName: string;
  email: string;
  password: string;
  currentLevelId: number;
  currentStudyLanguage: string;
  sendTips?: boolean;
  spokenOnly?: boolean;
};

const parseRegistrationFormValues = (
  values: registrationFormValues
): parsedRegistrationFormValues => {
  // Have to disable eslint rule to remove properties from object this way
  const {
    confirmPassword, // eslint-disable-line @typescript-eslint/no-unused-vars
    preference, // eslint-disable-line @typescript-eslint/no-unused-vars
    agreedPrivacyPolicy, // eslint-disable-line @typescript-eslint/no-unused-vars
    ...parsedValues
  } = values;

  return parsedValues;
};

function NewSignUp(props: { setLoggedIn: (values: any) => void }) {
  const { setLoggedIn } = props;
  const { search } = useLocation();
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation("new_sign_up");
  const { errors, handleError, closeError, closeAllErrors } = useErrors();
  const { courseDetails } = useParams();
  const elements = useElements();
  const stripe = useStripe();

  const { createSubscription } = useStripeSubscription();

  // STATE MANAGEMENT
  const [freeTrialModalVisible, setFreeTrialModalVisible] = useState(false);
  // for Step 2
  const [currency, setCurrency] = useState<string | null>(null);
  const [programType, setProgramType] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [studyLanguageId, setStudyLanguageId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // This is for styling the chosen program.
  // As well as persisting the program choice from step 1 to step 2.
  const [chosenProgram, setChosenProgram] = useState<Program>({
    id: "",
    name: "",
    price: 0,
    filter: [],
    currency: "",
    credits: 0,
    costCategory: "",
    packageDuration: 28,
    type: "",
  });

  // for the stepper
  const [step, setStep] = useState(0);

  // for loading state of submit butons
  const [loading, setLoading] = useState(false);

  // for loading state of social buttons
  const [socialLoadings, setSocialLoadings] = useState({
    google: false,
    facebook: false,
  });

  // Registration survey
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);
  const [registrationSurveyId, setRegistrationSurveyId] = useState<
    number | null
  >(null);

  // T&C Modal
  const [tcModalVisible, showTCModal, closeTCModal] = useModal();
  const [pendingPaymentData, setPendingPaymentData] =
    useState<PendingPaymentData | null>(null);

  const scrollOption: Options = {
    behavior: "smooth",
    block: "center",
  };

  const location = useLocation();
  const showPaymentMethod = !location.pathname.includes("/vi");

  // MUTATIONS AND QUERIES
  const [register] = useMutation(STUDENT_REGISTRATION);
  const [registerWithOauth] = useMutation(STUDENT_REGISTRATION_WITH_OAUTH);
  const [updateMySettings] = useMutation(UPDATE_PROFILE);
  const [createOneTimePayment] = useMutation(CREATE_ONE_TIME_PAYMENT);
  const [setPmAsDefault] = useMutation(SET_PM_AS_DEFAULT);
  const [createTrialPackage] = useMutation(CREATE_TRIAL_PACKAGE);
  const [createVnpayPayment] = useMutation(VNPAY_CREATE_PAYMENT);
  i18n.on("languageChanged", (lng) =>
    form.setFieldsValue({
      locale: lng,
    })
  );

  const [getChoosenProgram, { data: programData }] = useLazyQuery(GET_PROGRAM, {
    variables: {
      productId,
      currency: currency?.toUpperCase(),
      language,
    },
  });

  // HELPERS
  const parseAdInfo = React.useCallback(() => {
    const gclid = new URLSearchParams(search).get("gclid");
    const fbclid = new URLSearchParams(search).get("fbclid");
    if (gclid) return `gclid ${gclid}`;
    if (fbclid) return `fbclid ${fbclid}`;
    return null;
  }, [search]);

  // Parse ref info from URL
  const parseRefInfo = React.useCallback(() => {
    const ref = new URLSearchParams(search).get("ref");
    if (ref) return ref;
    return null;
  }, [search]);

  // Update user class type: 1-on-1 or group & study language: 1 -> 6
  const updateUserSettings = React.useCallback(
    async (userClassType: string, languageId: string) => {
      const activeClassType = CLASS_TYPES.find(
        (classType: { code: number; name: string }) => {
          return classType.name.toLowerCase().includes(userClassType);
        }
      );

      if (activeClassType) {
        await updateMySettings({
          variables: {
            input: {
              attributes: {
                preferClassType: activeClassType.code,
                currentLanguageId: languageId,
              },
            },
          },
        });
      }
    },
    [updateMySettings]
  );

  const { data: studiedLanguageData } = useQuery(GET_LANGUAGES);
  const studiedLanguageDataIdList = studiedLanguageData?.languages.map(
    (lang: any) => parseInt(lang.id, 10)
  );

  const handleSurveyCreation = (id: number) => {
    setRegistrationSurveyId(id);
  };

  const handleSurveyCompletion = () => {
    setIsSurveyCompleted(true);
  };

  const parseUrlParams = React.useCallback(
    (params: string): void => {
      const validateParam = (paramName: string, paramValue: string) => {
        switch (paramName) {
          case "language":
            return ["de", "en", "fr", "it", "jp", "vi"].includes(paramValue);
          case "studyLanguageId":
            return studiedLanguageDataIdList?.includes(
              parseInt(paramValue, 10)
            );
          case "programType":
            return ["1on1", "group"].includes(paramValue);
          case "currency":
            return currencies.includes(paramValue);
          default:
            return false;
        }
      };
      // Params Form: product_ID-language-study_language_id-currency
      // E.g for trial product:  http://localhost:3001/flexi-registration/prod_LDKNhsCJ1AQgfc-en-6-1on1-usd
      // E.g for nontrial product: http://localhost:3001/flexi-registration/prod_I5JW3kgo3JkQ9r-en-6-1on1-usd
      const details: string[] = params.split("-");
      if (details.length === 5) {
        setProductId(details[0]);
        if (validateParam("language", details[1].toLowerCase()))
          setLanguage(details[1].toLowerCase());
        if (validateParam("studyLanguageId", details[2].toLowerCase()))
          setStudyLanguageId(details[2].toLowerCase());
        if (validateParam("programType", details[3].toLowerCase()))
          setProgramType(
            details[3].toLowerCase() === "1on1" ? "1-on-1" : "group"
          ); // NOTE: The conversion is needed for the check before updating user's setting
        if (validateParam("currency", details[4].toLowerCase()))
          setCurrency(details[4].toLowerCase());
      }
    },
    [studiedLanguageDataIdList]
  );
  useEffect(() => {
    if (productId && language && currency) {
      getChoosenProgram();
    }
  }, [currency, productId, language, getChoosenProgram]);

  const handleBuyPackageProduct = async (
    paymentMethodId: string,
    promotionCode: string
  ) => {
    try {
      const adInfo = localStorage.getItem("adInfo") || null;
      const {
        data: {
          createOneTimePayment: { invoice },
        },
      } = await createOneTimePayment({
        variables: {
          input: {
            attributes: {
              items: Array.from(
                { length: 1 }, // Because this is package product, we only have one item
                () => chosenProgram.id
              ),
              paymentMethodId,
              promotionCode,
              adInfo,
              amount: chosenProgram.credits,
              expiredAt: moment()
                .add(chosenProgram.packageDuration, "days")
                .format("YYYY-MM-DD HH:mm:ss"),
              creditType: "full_course",
              price: chosenProgram.price,
              currency: chosenProgram.currency,
              stripePriceId: chosenProgram.id,
            },
          },
        },
      });

      const { id, total, customer } = invoice;

      if (invoice.status === "paid") {
        trackTapfiliateConversion(
          id,
          total * 0.01,
          customer,
          chosenProgram.currency
        );
      }

      if (invoice.status !== "paid") {
        const { paymentIntent } = invoice;

        if (paymentIntent.status === "requires_action") {
          if (!stripe || !elements) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
          }

          const result = await stripe.confirmCardPayment(
            paymentIntent.clientSecret
          );
          if (result.error) {
            throw result.error;
          } else if (result?.paymentIntent?.status === "succeeded") {
            trackTapfiliateConversion(
              id,
              total * 0.01,
              customer,
              chosenProgram.currency
            );
          } else {
            throw new Error(
              "Something wrong happened. Your payment cannot be processed."
            );
          }
        }
      }
    } catch (e) {
      handleError(e);
    }
  };

  const handleSetPmAsDefault = async (paymentMethodId: string) => {
    const adInfo = localStorage.getItem("adInfo") || null;
    await setPmAsDefault({
      variables: {
        input: {
          attributes: {
            paymentMethodId,
            adInfo,
          },
        },
      },
    });
  };

  const handleTrialPackageProduct = async (
    paymentMethodId: string,
    credits: number,
    promotionCode: string
  ) => {
    try {
      await createTrialPackage({
        variables: {
          input: {
            attributes: {
              priceId: chosenProgram.id,
              currency: chosenProgram.currency,
              languageId: studyLanguageId,
              packageDuration: chosenProgram.packageDuration,
              paymentMethodId,
              credits,
              promotionCode,
            },
          },
        },
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handleVnpayCreatePayment = async (values: any): Promise<void> => {
    localStorage.setItem("fromRegister", "true");
    const amount = chosenProgram.price;
    const referralCode = values.promoCode;
    // Store credits info into VnpayOrder, then create at vnpay_controller
    const orderDesc = {
      productName: chosenProgram.name,
      amount: chosenProgram.credits,
      expiredAt: moment()
        .add(chosenProgram.packageDuration, "days")
        .format("YYYY-MM-DD HH:mm:ss"),
      creditType: "full_course",
      price: chosenProgram.price,
      currency: chosenProgram.currency,
      stripePriceId: chosenProgram.id,
    };
    const returnUrl = `${window.location.origin}/return_url`;

    try {
      const {
        data: {
          vnpayCreatePayment: { url },
        },
      } = await createVnpayPayment({
        variables: {
          input: {
            amount,
            orderDesc,
            returnUrl,
            referralCode,
          },
        },
      });

      window.location.href = `${url}`;
    } catch (e) {
      handleError(e);
    }
  };

  // scroll to top of the page after going between step
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // check gclid and fbclid on url and save it to localStorage
  useEffect(() => {
    const adInfo = parseAdInfo();
    if (adInfo) {
      localStorage.setItem("adInfo", adInfo);
    } else {
      localStorage.removeItem("adInfo");
    }
  }, [parseAdInfo]);

  // check ref on url and save it to localStorage
  useEffect(() => {
    const ref = parseRefInfo();
    if (ref) {
      localStorage.setItem("ref", ref);
    } else {
      localStorage.removeItem("ref");
    }
  }, [parseRefInfo]);

  useEffect(() => {
    if (courseDetails) {
      // First parse course information from params
      parseUrlParams(courseDetails);
    }
  }, [courseDetails, parseUrlParams]);

  useEffect(() => {
    if (language) i18n.changeLanguage(language);
  }, [language, i18n]);

  // HANDLER FOR STEP 1
  const handleSubmitRegister = async (values: any): Promise<void> => {
    const adInfo = localStorage.getItem("adInfo") || null;
    const ref = localStorage.getItem("ref") || null;
    try {
      setLoading(true);

      const urlRegister = window.location.href;
      const { data: response } = await register({
        variables: parseRegistrationFormValues({
          ...values,
          currentLevelId: 1, // NOTE: the currentLevelID will be set at step 3: Choose Level
          adInfo,
          urlRegister,
          registrationSurveyId,
          role: 1,
          ref,
        }),
      });

      const {
        studentRegistration: { credentials, authenticatable: userData },
      } = response;

      authenticate(credentials, userData);
      setLanguageDataVar({
        studyLanguageId: studyLanguageId || "",
      });
      closeAllErrors();

      // handle show trial popup
      if (programData?.plan?.filter.includes("trial")) {
        setFreeTrialModalVisible(true);
      } else {
        setStep(1);
      }

      // Track new user registration succesfully to GTM
      trackNewUserRegistration(userData?.email);

      // Track add to cart to Google Analytics Ecommerce
      trackAddToCartEvent(userData?.email);
    } catch (e) {
      setLoading(false);
      handleError(e);
    } finally {
      // After user is registered,
      // set their languageId and preferClassType
      if (programType && studyLanguageId) {
        updateUserSettings(programType, studyLanguageId);
      }
      setLoading(false);
    }
  };

  const handleProceedTrialPopup = () => {
    setFreeTrialModalVisible(false);
    setStep(1);
  };

  const handleOauthLogin = async (token: string, provider: string) => {
    const adInfo = localStorage.getItem("adInfo") || null;
    try {
      setSocialLoadings({ ...socialLoadings, [provider]: true });
      const urlRegister = window.location.href;
      const { data: response } = await registerWithOauth({
        variables: {
          token,
          currentLevelId: 1,
          adInfo,
          urlRegister,
          registrationSurveyId,
          role: 1,
        },
      });
      const {
        studentRegistrationWithOauth: {
          credentials,
          authenticatable: userData,
        },
      } = response;

      authenticate(credentials, userData);
      setLanguageDataVar({
        studyLanguageId: studyLanguageId || "",
      });
      closeAllErrors();

      // After user is registered,
      // set their languageId and preferClassType
      if (programType && studyLanguageId) {
        updateUserSettings(programType, studyLanguageId);
      }

      // handle show trial popup
      if (programData?.plan?.filter.includes("trial")) {
        setFreeTrialModalVisible(true);
      } else {
        setStep(1);
      }
      // Track new user registration succesfully to GTM
      trackNewUserRegistration(userData?.email);

      // Track add to cart to Google Analytics Ecommerce
      trackAddToCartEvent(userData?.email);
    } catch (error) {
      handleError(error);
    }
    setSocialLoadings({ ...socialLoadings, [provider]: false });
  };

  // HANDLER FOR STEP 2
  const handleSubmitPaymentInfo = async (values: any): Promise<void> => {
    // Track add payment info
    const user = getUserData();
    trackAddPaymentInfo(chosenProgram, user?.email);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
      billing_details: {
        name: values.cardholderName,
        email: user?.email,
        address: {
          line1: values?.billingLine1,
          city: values?.billingCity,
          postal_code: values?.billingPostalCode,
          country: values?.billingCountry,
        },
      },
    });

    if (error) {
      handleError(new Error(t("Stripe could not process your card's info")));
      return;
    }

    const paymentMethodId = paymentMethod?.id;
    if (!paymentMethodId) return;

    // Store payment data and show T&C modal (no loading needed for modal display)
    setPendingPaymentData({
      paymentMethodId,
      promoCode: values.promoCode,
      user,
    });

    showTCModal();
  };

  // HANDLER FOR PROCESSING PAYMENT AFTER T&C AGREEMENT
  const handleProcessPaymentAfterTCAgreement = async (): Promise<void> => {
    if (!pendingPaymentData) return;

    try {
      setLoading(true);
      let hasError = false;

      const { paymentMethodId, promoCode, user } = pendingPaymentData;

      // Add default payment method to user (moved here from initial step)
      await handleSetPmAsDefault(paymentMethodId);

      // get adInfo from localStorage
      const adInfo = localStorage.getItem("adInfo") || null;

      if (chosenProgram.type.includes("subscription")) {
        // Create the subscription
        try {
          await createSubscription({
            priceId: chosenProgram.id,
            adInfo,
            paymentMethodId,
            promotionCode: promoCode,
          });

          // If the user doesn't choose a trial program, track the purchase credits event for Google Ads Conversion
          // and the purchase event for Google Analytics ecommerce report.
          if (!chosenProgram.filter.includes("trial")) {
            trackPurchase(chosenProgram, user?.email);
          }
        } catch (e) {
          hasError = true;
          handleError(e);
        }
      } else if (chosenProgram.type.includes("package")) {
        // Create the one-time purchase
        if (chosenProgram.filter.includes("trial")) {
          await handleTrialPackageProduct(
            paymentMethodId,
            chosenProgram.credits,
            promoCode
          );
        } else {
          try {
            await handleBuyPackageProduct(paymentMethodId, promoCode);

            // Track purchase credits succesfully to Google Ads + Google Analytics
            trackPurchase(chosenProgram, user?.email);
          } catch (e) {
            hasError = true;
            handleError(e);
          }
        }
      }

      // Tracking new user registration with credit card successfully to GTM
      if (!hasError) {
        trackNewUserRegistrationWithCreditCard(user?.email);

        message.success(t("Payment Successful!"), 3);
        setPaymentSuccess(true);
        // Postpone redirect for few seconds so that users can see the success message
        setTimeout(() => {
          setStep(2);
        }, 3000);
      }
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
      closeTCModal();
      setPendingPaymentData(null);
    }
  };

  // HANDLER FOR T&C MODAL CANCELLATION
  const handleTCModalCancel = () => {
    closeTCModal();
    setPendingPaymentData(null);
    setLoading(false);
  };

  const renderSteps = () => {
    if (step === 0) {
      return (
        <>
          <Form
            name="register"
            form={form}
            onFinish={handleSubmitRegister}
            initialValues={{
              charactersPreference: "simplified",
            }}
            scrollToFirstError={scrollOption}
          >
            <Row justify="center" align="top">
              <Col xs={24} md={13} xl={7}>
                {programType &&
                language &&
                studyLanguageId &&
                currency &&
                productId ? (
                  <>
                    <SocialLogin
                      type="signup"
                      loading={socialLoadings}
                      handleOauthLogin={handleOauthLogin}
                    />
                    <SignUpForm loading={loading} />
                  </>
                ) : (
                  <p style={{ color: "#fff", fontWeight: "bold" }}>
                    {t("The URL is incorrect. Please try again!")}
                  </p>
                )}
              </Col>
            </Row>
          </Form>
          <FreeTrialModal
            visible={freeTrialModalVisible}
            onCancel={() => handleProceedTrialPopup()}
            onConfirm={() => handleProceedTrialPopup()}
          />
        </>
      );
    }

    if (step === 1) {
      return (
        <Form
          name="register"
          form={form}
          onFinish={
            showPaymentMethod
              ? handleSubmitPaymentInfo
              : handleVnpayCreatePayment
          }
          initialValues={{
            charactersPreference: "simplified",
          }}
          scrollToFirstError={scrollOption}
        >
          <Row justify="center" align="top">
            <Col xs={24} md={10} lg={22}>
              {programType &&
                studyLanguageId &&
                currency &&
                productId &&
                language && (
                  <PaymentForm
                    paymentSuccess={paymentSuccess}
                    language={language}
                    productId={productId}
                    loading={loading}
                    programType={programType}
                    studyLanguageId={studyLanguageId}
                    currency={currency}
                    chosenProgram={chosenProgram}
                    setChosenProgram={setChosenProgram}
                  />
                )}
            </Col>
          </Row>
        </Form>
      );
    }

    if (step === 2) {
      return <AfterRegistration setLoggedIn={setLoggedIn} />;
    }
    return null;
  };

  return (
    <PageContent style={{ width: "100vw" }}>
      {isSurveyCompleted ? (
        <Row justify="center">
          <Col
            xs={24}
            sm={24}
            md={{ span: 12, offset: 1 }}
            lg={{ span: 12, offset: 0 }}
            xl={8}
          >
            <CustomSteps size="small" current={step} direction="horizontal">
              <CustomStep title={t("Sign Up")} />
              <CustomStep title={t("Details")} />
              <CustomStep title={t("Learn")} />
            </CustomSteps>
          </Col>
        </Row>
      ) : null}
      <Row justify="center" align="top">
        <Col xs={24} md={13} xl={7}>
          {errors && (
            <div>
              <br />
              <ErrorAlerting errors={errors} onClose={closeError} />
            </div>
          )}
        </Col>
      </Row>

      {!isSurveyCompleted ? (
        <RegistrationSurvey
          onComplete={handleSurveyCompletion}
          onSurveyCreated={handleSurveyCreation}
          studyLanguageId={studyLanguageId || ""}
        />
      ) : (
        renderSteps()
      )}

      <TermsConditionsModal
        visible={tcModalVisible}
        onAgree={handleProcessPaymentAfterTCAgreement}
        onCancel={handleTCModalCancel}
        loading={loading}
      />
    </PageContent>
  );
}

export default NewSignUp;
